import { useEffect, useState } from "react";
import { CalendarCheck, Hourglass, CheckCircle2, XCircle, Scissors } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { LINKS, BUSINESS_NAME, openExternal, waIntent, WHATSAPP_DIGITS } from "@/constants/links";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_UI = {
  confermata: { icon: CheckCircle2, cls: "text-emerald-400", label: "Prenotazione confermata" },
  attesa: { icon: Hourglass, cls: "text-amber-400", label: "In lista d'attesa" },
  annullata: { icon: XCircle, cls: "text-red-400", label: "Prenotazione annullata" },
  completata: { icon: CheckCircle2, cls: "text-emerald-400", label: "Servizio completato" },
  assente: { icon: XCircle, cls: "text-orange-400", label: "Appuntamento mancato" },
};

const PAY_UI = {
  segnalato: "💳 Pagamento in verifica",
  pagato: "✅ Pagato",
  in_salone: "🤝 Paghi in salone",
};

// Página pública /cita/{id}: el cliente guarda este link como promemoria de su cita
// (resumen + estado + pago), sin mostrar su teléfono.
export default function BookingStatusPage({ bookingId }) {
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/booking/${bookingId}/status`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setNotFound(true));
  }, [bookingId]);

  const ui = data ? STATUS_UI[data.status] || STATUS_UI.confermata : null;
  const Icon = ui?.icon || CalendarCheck;

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-zinc-950 px-6 text-white">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-zinc-900 p-6 text-center shadow-2xl">
        <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/15 text-amber-400">
          <Scissors size={22} />
        </span>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
          {BUSINESS_NAME}
        </p>
        <h1 className="mb-4 font-display text-xl font-bold">Il tuo appuntamento</h1>
        {notFound ? (
          <p data-testid="booking-status-notfound" className="text-sm text-zinc-400">
            Prenotazione non trovata o già archiviata.
          </p>
        ) : !data ? (
          <p className="text-sm text-zinc-500">Caricamento…</p>
        ) : (
          <div data-testid="booking-status-card" className="flex flex-col items-center gap-3">
            <span className={`flex h-14 w-14 items-center justify-center rounded-full bg-white/5 ${ui.cls}`}>
              <Icon size={28} />
            </span>
            <p data-testid="booking-status-label" className={`text-sm font-extrabold uppercase tracking-widest ${ui.cls}`}>
              {ui.label}
            </p>
            <div className="w-full rounded-2xl border border-white/10 bg-zinc-950/60 p-4 text-left">
              <p className="text-sm font-bold text-white">{data.nome}</p>
              <p className="mt-1 text-sm text-zinc-300">
                {data.servizio} — {data.giorno} alle {data.ora}
              </p>
              {data.barbiere && (
                <p className="mt-0.5 text-xs text-zinc-500">con {data.barbiere}</p>
              )}
              {data.payment && PAY_UI[data.payment] && (
                <p data-testid="booking-status-payment" className="mt-2 text-xs font-bold text-zinc-300">
                  {PAY_UI[data.payment]}
                </p>
              )}
            </div>
            <a
              data-testid="booking-status-whatsapp"
              href={LINKS.whatsapp}
              onClick={openExternal(LINKS.whatsapp, waIntent(WHATSAPP_DIGITS, "Ciao! Ho una prenotazione e vorrei scrivervi"))}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] py-3 text-xs font-extrabold uppercase tracking-widest text-white transition-colors hover:brightness-110"
            >
              <FaWhatsapp size={16} /> Scrivici su WhatsApp
            </a>
            <a
              data-testid="booking-status-home"
              href="/"
              className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 transition-colors hover:text-amber-400"
            >
              Torna all&apos;app
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
